import {
  faChartPie,
  faFileCsv,
  faPen,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AssistChip, Button, CopyableText, Stack, Text } from '@sixui/ui';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { QuerySkeleton } from '~/components/atoms/QuerySkeleton';
import { Breadcrumbs } from '~/components/molecules/Breadcrumbs';
import { Heading } from '~/components/molecules/Heading';
import { getPollInfo } from '~/components/molecules/PollListItem/getPollInfo';
import { AppBreadcrumbs } from '~/components/organisms/Breadcrumbs';
import { Poll } from '~/components/organisms/Poll';
import { useAppRegion } from '~/hooks/useAppRegion';
import { usePollQuery } from '~/lib/api/modules/Polls/hooks/usePollQuery';
import { appRegionsSettings } from '~/lib/appSettings';

export type IAppPollPageProps = {
  appId: string;
  pollId: string;
};

export const AppPollPage: React.FC<IAppPollPageProps> = (props) => {
  const { appId, pollId } = props;
  const { t } = useTranslation();
  const pollQuery = usePollQuery({
    appId,
    pollId,
  });
  const appRegion = useAppRegion();
  const appRegionSettings = appRegionsSettings[appRegion];

  const poll = pollQuery.data;
  const parentUrl = `/apps/${appId}/polls`;
  const pollInfo = poll ? getPollInfo(poll) : undefined;

  return (
    <>
      <Helmet>
        <title>{t('AppPollPage.seo.title')}</title>
      </Helmet>

      <AppBreadcrumbs appId={appId}>
        <Breadcrumbs.Action href={parentUrl}>
          <FontAwesomeIcon icon={faChartPie} />
          {t('AppPollsPage.title')}
        </Breadcrumbs.Action>
      </AppBreadcrumbs>

      <Heading
        title={
          <QuerySkeleton query={pollQuery} length={{ min: 8, max: 16 }}>
            <Stack horizontal gap={3}>
              {t('AppPollPage.title', { name: pollQuery.data?.title })}
              {pollInfo && (
                <AssistChip label={t(`PollInfo.${pollInfo.status}`)} />
              )}
            </Stack>
          </QuerySkeleton>
        }
        subtitle={
          <Text variant="body" size="md">
            {t('AppPollPage.subtitle')} <CopyableText>{pollId}</CopyableText>
          </Text>
        }
        previousUrl={parentUrl}
        actions={
          <Stack horizontal gap={3}>
            <Button
              href={`/apps/${appId}/polls/${pollId}/update`}
              icon={<FontAwesomeIcon icon={faPen} />}
            >
              {t('common.edit')}
            </Button>
            <QuerySkeleton query={pollQuery} variant="circular">
              <Button
                href={`${appRegionSettings.apiUrl}/admin/press/polls/${pollId}/export?appId=${appId}&exportToken=${poll?.exportToken}`}
                icon={<FontAwesomeIcon icon={faFileCsv} />}
              >
                {t('AppPollPage.export')}
              </Button>
            </QuerySkeleton>
          </Stack>
        }
      />

      <Poll appId={appId} pollId={pollId} />
    </>
  );
};
